import type { DateFormat, Locale } from "@porsche-customer/slfinrtl-context-types"
import { allMarketplaceKeys } from "@porsche-customer/slfinrtl-context-types"
import type { NewsImageView } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useIntl } from "react-intl"
import { DatepickerField, FormLayout, FormLayoutGroup, InputField, MultiSelectField } from "src/components"
import { useMarketplaceName } from "../hooks/useMarketplaceName/useMarketplaceName"
import { NewsContentField } from "./NewsContentField"
import { NewsImagesField } from "./NewsImagesField"
import { newsEditorMessages as messages } from "./newsEditor.messages"

interface NewsEditorFormProps {
	dateFormat: DateFormat
	/** the saved images of the entry */
	images: NewsImageView[]
	/** shows the values without the possibility to change them */
	isReadOnly?: boolean
	locale: Locale
	onFirstImageUrlChange: (url: string | undefined) => void
	onUploadingImagesChange: (isUploading: boolean) => void
}

export const NewsEditorForm = ({
	dateFormat,
	images,
	isReadOnly,
	locale,
	onFirstImageUrlChange,
	onUploadingImagesChange,
}: NewsEditorFormProps) => {
	const { formatMessage } = useIntl()
	const getMarketplaceName = useMarketplaceName()
	const disabledHint = isReadOnly ? formatMessage(messages.readOnlyHint) : undefined

	const marketplaceOptions = useMemo(
		() =>
			allMarketplaceKeys
				.map((key) => ({ label: `${getMarketplaceName(key)} (${key.toUpperCase()})`, value: key }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[getMarketplaceName],
	)

	return (
		<FormLayoutGroup>
			<FormLayout>
				<InputField
					description={formatMessage(messages.sourceLanguage)}
					disabledHint={disabledHint}
					label={formatMessage(messages.title)}
					name="title"
					required
					type="text"
				/>
			</FormLayout>
			<FormLayout>
				<NewsContentField disabledHint={disabledHint} name="content" />
			</FormLayout>
			<FormLayout>
				<NewsImagesField
					disabledHint={disabledHint}
					images={images}
					name="imageIds"
					onFirstImageUrlChange={onFirstImageUrlChange}
					onUploadingChange={onUploadingImagesChange}
				/>
			</FormLayout>
			<FormLayout>
				<MultiSelectField
					disabledHint={disabledHint}
					label={formatMessage(messages.marketplaces)}
					name="marketplaceKeys"
					options={marketplaceOptions}
					required
					selectAllLabel={formatMessage(messages.allMarketplaces)}
				/>
			</FormLayout>
			<FormLayout>
				<DatepickerField
					data-testid="news-planned-publication-date"
					dateFormat={dateFormat}
					disabledHint={disabledHint}
					label={formatMessage(messages.plannedPublicationDate)}
					locale={locale}
					name="plannedPublicationDate"
					required
				/>
			</FormLayout>
		</FormLayoutGroup>
	)
}
